import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Command, CornerDownLeft, GanttChartSquare, Gauge, KanbanSquare, Keyboard, ListChecks, Search, Waypoints, X } from "lucide-react";
import {
  hasAnyRole,
  isReadOnlyExecutive,
  isWallDisplayOnly,
  OperationsData,
  Roles,
  SchedulePlanners,
  type Role
} from "../../auth/roles";
import { useShownRoles } from "../../auth/useShownRoles";
import { navFor } from "../hub/hubs";
import { focusFirstPageSearchInput, GLOBAL_SHORTCUTS, useGlobalShortcuts } from "./useGlobalShortcuts";

export interface CommandPaletteAction {
  id: string;
  label: string;
  group: string;
  to: string;
  shortcut?: string;
  keywords: string;
  allow: readonly Role[];
}

const FLOOR_AND_PLANNING_COMMANDS: readonly CommandPaletteAction[] = [
  {
    id: "cmd-floor-command",
    label: "Floor Command — live shop status & decisions queue",
    group: "Floor & Planning",
    to: "/floor-command",
    shortcut: "F",
    keywords: "floor command live map tiles bottleneck hot jobs decisions",
    allow: [Roles.OperationsManager, Roles.Planner, Roles.Admin]
  },
  {
    id: "cmd-shop-kanban",
    label: "Shop Kanban board — all shops",
    group: "Floor & Planning",
    to: "/shop-floor/kanban",
    shortcut: "K",
    keywords: "kanban board lanes ready queued in process waiting osp done today",
    allow: OperationsData
  },
  {
    id: "cmd-order-gantt",
    label: "Order Gantt — finite schedule & critical chain",
    group: "Floor & Planning",
    to: "/planning/orders",
    shortcut: "G",
    keywords: "gantt order timeline finite schedule drag drop",
    allow: OperationsData
  },
  {
    id: "cmd-scenarios-rebalance",
    label: "Scenario Lab & VIPER Rebalance",
    group: "Floor & Planning",
    to: "/planning/scenarios",
    shortcut: "R",
    keywords: "rebalance scenario what-if overtime shift move",
    allow: SchedulePlanners
  },
  {
    id: "cmd-dispatch-lists",
    label: "Dispatch lists by rule (CR · EDD · Slack)",
    group: "Floor & Planning",
    to: "/shop-floor/dispatch",
    keywords: "dispatch list critical ratio edd slack workcenter",
    allow: OperationsData
  }
];

/**
 * Returns the navigation actions permitted for the caller's role set (D-O76).
 * Exported so unit tests can verify role-based command filtering deterministically.
 */
export function commandsForRoles(roles: readonly string[]): CommandPaletteAction[] {
  if (!roles || roles.length === 0 || isWallDisplayOnly(roles)) {
    return [];
  }

  const readOnlyExec = isReadOnlyExecutive(roles);
  const actions: CommandPaletteAction[] = [];
  const seenPaths = new Set<string>();

  for (const cmd of FLOOR_AND_PLANNING_COMMANDS) {
    if (!hasAnyRole(roles, cmd.allow)) continue;
    if (readOnlyExec && cmd.to !== "/operations") continue;
    actions.push(cmd);
    seenPaths.add(cmd.to);
  }

  for (const section of navFor(roles)) {
    for (const item of section.items) {
      if (seenPaths.has(item.to)) continue;
      seenPaths.add(item.to);
      actions.push({
        id: `nav-${item.id}`,
        label: `${item.label} (${section.heading})`,
        group: section.heading,
        to: item.to,
        keywords: `${item.label} ${section.heading} ${item.to}`.toLowerCase(),
        allow: item.allow
      });
    }
  }

  return actions;
}

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  onOpenShortcuts?: () => void;
  roles?: readonly string[];
}

export function CommandPalette({ open, onClose, onOpenShortcuts, roles: rolesOverride }: CommandPaletteProps) {
  const shownRoles = useShownRoles();
  const effectiveRoles = rolesOverride ?? shownRoles;
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const titleId = useId();
  const listboxId = useId();

  const permitted = useMemo(() => commandsForRoles(effectiveRoles), [effectiveRoles]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return permitted;
    return permitted.filter(
      (a) =>
        a.label.toLowerCase().includes(q) ||
        a.group.toLowerCase().includes(q) ||
        a.keywords.toLowerCase().includes(q) ||
        a.to.toLowerCase().includes(q)
    );
  }, [permitted, query]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  if (!open) return null;

  const runAction = (action: CommandPaletteAction | undefined) => {
    if (!action) return;
    onClose();
    navigate(action.to);
  };

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (filtered.length > 0) {
        setActiveIndex((prev) => (prev + 1) % filtered.length);
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (filtered.length > 0) {
        setActiveIndex((prev) => (prev - 1 + filtered.length) % filtered.length);
      }
    } else if (e.key === "Enter") {
      e.preventDefault();
      runAction(filtered[activeIndex]);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[85] flex items-start justify-center bg-slate-900/45 px-4 pt-16 backdrop-blur-[1px]"
      onClick={onClose}
      data-testid="command-palette-backdrop"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="command-palette"
        className="w-full max-w-xl overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-2.5">
          <Search aria-hidden="true" className="h-4 w-4 flex-none text-slate-500" />
          <label htmlFor={`${listboxId}-input`} id={titleId} className="sr-only">
            Command palette — search screens and floor actions
          </label>
          <input
            id={`${listboxId}-input`}
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls={listboxId}
            aria-autocomplete="list"
            placeholder="Jump to a screen or floor command (G Gantt, K Kanban, F Floor Command)…"
            className="w-full border-0 bg-transparent text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="command-palette-input"
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close command palette"
            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            data-testid="command-palette-close"
          >
            <X aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>

        <ul
          id={listboxId}
          role="listbox"
          aria-label="Available navigation commands"
          className="max-h-80 divide-y divide-slate-100 overflow-y-auto p-1.5"
          data-testid="command-palette-list"
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-6 text-center text-xs text-slate-500" data-testid="command-palette-empty">
              No permitted screens match “{query}”.
            </li>
          ) : (
            filtered.map((item, idx) => {
              const active = idx === activeIndex;
              return (
                <li
                  key={item.id}
                  role="option"
                  aria-selected={active}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => runAction(item)}
                  data-testid={`command-item-${item.id}`}
                  className={`flex cursor-pointer items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                    active ? "bg-brand-50 text-brand-900" : "text-slate-800 hover:bg-slate-50"
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{item.label}</div>
                    <div className="truncate text-[11px] text-slate-500">
                      {item.group} · {item.to}
                    </div>
                  </div>
                  <div className="flex flex-none items-center gap-1.5">
                    {item.shortcut && (
                      <kbd className="rounded border border-slate-300 bg-white px-1.5 py-0.5 font-mono text-[11px] font-semibold text-slate-700">
                        {item.shortcut}
                      </kbd>
                    )}
                    <CornerDownLeft aria-hidden="true" className="h-3.5 w-3.5 text-slate-400" />
                  </div>
                </li>
              );
            })
          )}
        </ul>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
          <div className="flex flex-wrap items-center gap-3">
            <span>
              <kbd className="rounded border border-slate-300 bg-white px-1 py-0.5 font-mono text-[10px]">↑↓</kbd> navigate
            </span>
            <span>
              <kbd className="rounded border border-slate-300 bg-white px-1 py-0.5 font-mono text-[10px]">Enter</kbd> open
            </span>
            <span>
              <kbd className="rounded border border-slate-300 bg-white px-1 py-0.5 font-mono text-[10px]">Esc</kbd> close
            </span>
          </div>
          {onOpenShortcuts && (
            <button
              type="button"
              onClick={() => {
                onClose();
                onOpenShortcuts();
              }}
              className="inline-flex items-center gap-1 font-medium text-brand-700 hover:underline"
              data-testid="command-palette-show-shortcuts"
            >
              <Keyboard aria-hidden="true" className="h-3 w-3" />
              All shortcuts (?)
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function ShortcutsHelpDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const titleId = useId();
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[85] flex items-center justify-center bg-slate-900/45 p-4"
      onClick={onClose}
      data-testid="shortcuts-help-backdrop"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="global-shortcuts-dialog"
        className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Escape" || e.key === "?") {
            e.preventDefault();
            onClose();
          }
        }}
      >
        <div className="flex items-center justify-between border-b border-slate-100 pb-2.5">
          <h2 id={titleId} className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <Keyboard aria-hidden="true" className="h-4 w-4 text-brand-700" />
            Floor &amp; Planning keyboard shortcuts
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close keyboard shortcuts"
            className="rounded p-1 text-slate-500 hover:bg-slate-100"
            data-testid="shortcuts-help-close"
          >
            <X aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>

        <dl className="mt-3 grid grid-cols-[auto,1fr] items-center gap-x-4 gap-y-2 text-xs">
          {GLOBAL_SHORTCUTS.map((s) => (
            <div key={s.key} className="contents">
              <dt>
                <kbd className="inline-flex min-w-[2.25rem] justify-center rounded border border-slate-300 bg-slate-50 px-1.5 py-0.5 font-mono text-xs font-semibold text-slate-800">
                  {s.label}
                </kbd>
              </dt>
              <dd className="text-slate-700">{s.description}</dd>
            </div>
          ))}
        </dl>

        <p className="mt-3 border-t border-slate-100 pt-2.5 text-[11px] text-slate-500">
          Single-letter shortcuts are disabled while typing in search boxes, forms, or notes.
        </p>
      </div>
    </div>
  );
}

let activeCommandLayerInstance: symbol | null = null;

/**
 * Single mountable `<CommandLayer />` for AppShell (and safe to include on Floor pages as a fallback before AppShell
 * is wired by the integrator — only the first mounted instance attaches listeners and dialogs).
 */
export function CommandLayer() {
  const navigate = useNavigate();
  const instanceId = useRef<symbol>(Symbol("CommandLayer"));
  const [isPrimary, setIsPrimary] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    if (activeCommandLayerInstance === null) {
      activeCommandLayerInstance = instanceId.current;
      setIsPrimary(true);
    }
    const currentId = instanceId.current;
    return () => {
      if (activeCommandLayerInstance === currentId) {
        activeCommandLayerInstance = null;
      }
    };
  }, []);

  const handleNavigate = useCallback(
    (to: string) => {
      setPaletteOpen(false);
      setHelpOpen(false);
      navigate(to);
    },
    [navigate]
  );

  const handleTogglePalette = useCallback(() => {
    setHelpOpen(false);
    setPaletteOpen((prev) => !prev);
  }, []);

  const handleToggleHelp = useCallback(() => {
    setPaletteOpen(false);
    setHelpOpen((prev) => !prev);
  }, []);

  useGlobalShortcuts({
    enabled: isPrimary,
    onNavigate: handleNavigate,
    onTogglePalette: handleTogglePalette,
    onToggleHelp: handleToggleHelp,
    onFocusSearch: focusFirstPageSearchInput
  });

  if (!isPrimary) return null;

  return (
    <>
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onOpenShortcuts={() => setHelpOpen(true)}
      />
      <ShortcutsHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
}

export { Command, GanttChartSquare, Gauge, KanbanSquare, ListChecks, Waypoints };
